import { z } from 'zod';
import '@apexdesigner/schema-tools';
import { belongsTo, options, references } from '@apexdesigner/schema-tools';

export const testResult = z
  .object({
    id: z.number().hidden().describe('Unique identifier'),

    type: options([
      { value: 'Test In', name: 'Test In' },
      { value: 'Test In Verification', name: 'Test In Verification' },
      { value: 'Test Out', name: 'Test Out' },
      { value: 'Test Out Verification', name: 'Test Out Verification' },
    ])
      .nullable()
      .optional()
      .describe(
        'Type - Legacy single test type; new results record cycle + stage instead',
      ),

    cycle: options([
      { value: 'Start of Cycle', name: 'Start of Cycle' },
      { value: 'End of Cycle', name: 'End of Cycle' },
    ])
      .nullable()
      .optional()
      .describe('Cycle - Start or end of the assessment cycle (opp 3648)'),

    stage: options([
      { value: 'Tutor', name: 'Tutor' },
      { value: 'Verification', name: 'Verification' },
      { value: 'Retest', name: 'Retest' },
    ])
      .nullable()
      .optional()
      .describe(
        'Stage - Tutor test, verification, or retest; shown as "Type" (opp 3648)',
      ),

    date: z.string().nullable().optional().presentAs('date').describe('Date'),

    achievedOralReadingFluency: z
      .string()
      .nullable()
      .optional()
      .describe('Achieved Oral Reading Fluency'),

    workingOralReadingFluency: z
      .string()
      .nullable()
      .optional()
      .describe('Working Oral Reading Fluency'),

    achievedCorrectWordsPerMinute: z
      .number()
      .nullable()
      .optional()
      .describe('Achieved Correct Words Per Minute'),

    achievedErrors: z
      .number()
      .nullable()
      .optional()
      .describe(
        'Achieved Errors - Errors alongside the achieved words-per-minute count',
      ),

    workingCorrectWordsPerMinute: z
      .number()
      .nullable()
      .optional()
      .describe('Working Correct Words Per Minute'),

    workingErrors: z
      .number()
      .nullable()
      .optional()
      .describe(
        'Working Errors - Errors alongside the working words-per-minute count',
      ),

    tattumStart: z
      .string()
      .nullable()
      .optional()
      .describe(
        'Tattum Start - Legacy free-text start level; kept for historical results',
      ),

    consonantSoundScore: z
      .number()
      .nullable()
      .optional()
      .describe(
        'Consonant Sound Score - Legacy; folded into decoding Level 1 on new results',
      ),

    vowelSoundScore: z
      .number()
      .nullable()
      .optional()
      .describe(
        'Vowel Sound Score - Legacy; folded into decoding Level 1 on new results',
      ),

    firstFailedLevel: z
      .number()
      .nullable()
      .optional()
      .describe(
        'First Failed Level - First decoding level failed at start of cycle (opp 5291)',
      ),

    decodingLevel1PartA: z
      .number()
      .nullable()
      .optional()
      .describe('Decoding Level 1 Part A'),

    decodingLevel1PartB: z
      .number()
      .nullable()
      .optional()
      .describe('Decoding Level 1 Part B'),

    decodingLevel2: z
      .number()
      .nullable()
      .optional()
      .describe('Decoding Level 2 - Rows passed, of 5 rows of 3 words'),

    decodingLevel3: z
      .number()
      .nullable()
      .optional()
      .describe('Decoding Level 3'),

    decodingLevel4: z
      .number()
      .nullable()
      .optional()
      .describe('Decoding Level 4'),

    decodingLevel5: z
      .number()
      .nullable()
      .optional()
      .describe('Decoding Level 5'),

    decodingLevel6: z
      .number()
      .nullable()
      .optional()
      .describe('Decoding Level 6'),

    decodingLevel7: z
      .number()
      .nullable()
      .optional()
      .describe('Decoding Level 7'),

    decodingLevel8: z
      .number()
      .nullable()
      .optional()
      .describe('Decoding Level 8'),

    decodingLevel9: z
      .number()
      .nullable()
      .optional()
      .describe('Decoding Level 9'),

    decodingLevel10: z
      .number()
      .nullable()
      .optional()
      .describe('Decoding Level 10'),

    decodingLevel11: z
      .number()
      .nullable()
      .optional()
      .describe('Decoding Level 11'),

    decodingLevel12: z
      .number()
      .nullable()
      .optional()
      .describe('Decoding Level 12'),

    decodingLevel13: z
      .number()
      .nullable()
      .optional()
      .describe('Decoding Level 13'),

    decodingLevel14: z
      .number()
      .nullable()
      .optional()
      .describe('Decoding Level 14'),

    decodingLevel15: z
      .number()
      .nullable()
      .optional()
      .describe('Decoding Level 15'),

    studentId: z.number().hidden().describe('Foreign key to Student'),

    appUserId: z
      .number()
      .nullable()
      .optional()
      .presentAs('reference')
      .describe('Foreign key to User'),

    achievedArticleId: z
      .number()
      .nullable()
      .optional()
      .presentAs('reference')
      .describe('Foreign key to TestArticle'),

    workingArticleId: z
      .number()
      .nullable()
      .optional()
      .presentAs('reference')
      .describe('Foreign key to TestArticle'),

    termId: z.number().presentAs('reference').describe('Foreign key to Term'),

    student: belongsTo('Student'),

    appUser: references('User'),

    achievedArticle: references('TestArticle'),

    workingArticle: references('TestArticle'),

    term: references('Term'),
  })
  .describe('A reading assessment recorded for a student.')
  .as('TestResult');

export { testResult as testResultSchema };
